import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme'

import { Station, Stationcategory, StationcategoryFactory } from '../../resource/';
import { IAuthProps, Query } from '../../services/';

import { Input } from '@independent-software/typeui/controls/Input';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Form } from '@independent-software/typeui/controls/Form';
import { DmsInput } from '@independent-software/typeui/modules/DmsInput';
import { PickLocationMap } from '../../modules/Map';
import { Section } from '../../modules';
import { CategoryTree } from '../../controls/CategoryTree';

interface IProps {
  /** Initial form data. */
  data: Station;
  /** Called whenever form changes. */
  onChange: (data: Station, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

interface IState {
  /** Current form data. */
  data: Station;
  stationcategories: Stationcategory[];
}

class StationForm extends React.Component<IAuthProps & IProps, IState> {
  constructor(props: IAuthProps & IProps) {
    super(props);

    // Intialize state.
    this.state = {
      data: props.data,
      stationcategories: []
    };    
  }

  componentDidMount = () => {
    // Retrieve a list of stationcategories:
    let query = new Query('name', 'asc');
    StationcategoryFactory.getSome(this.props.auth, 0, 999, query)
      .then((res) => this.setState({ stationcategories: res.items }));    
  }

  renderName = () =>
    <Form.Field 
      name="name" label="Name"
      value={this.state.data.name}
      control={(<Input type="text" maxLength={20} placeholder="Name" fluid/>)}
      required={{message: "A station name is required."}}
      maxLength={{length: 20, message: "Station name may not have more than 20 characters."}}
      hint={(<React.Fragment>Please enter a station name.</React.Fragment>)}/>

  renderDescription = () =>
    <Form.Field 
      name="description" label="Description"
      value={this.state.data.description}
      control={(<Input type="text" placeholder="Description" fluid/>)}
      hint={(<React.Fragment>Please enter a station description.</React.Fragment>)}/>

  renderLatitude = () => 
    <Form.Field 
      forceupdate
      name="latitude" label="Latitude"
      value={this.state.data.latitude}
      control={(<DmsInput isLatitude={true}/>)}
      isFloat={{message: "Station latitude must be a numeric value."}}
      between={{min: -90, max: 90, message: "Station latitude must be between -90 and 90."}}
      hint={(<React.Fragment>Please enter latitude either as degrees, minutes, seconds or as a decimal.</React.Fragment>)}/>            

  renderLongitude = () =>
    <Form.Field 
      forceupdate
      name="longitude" label="Longitude"
      value={this.state.data.longitude}
      control={(<DmsInput isLatitude={false}/>)}
      isFloat={{message: "Station longitude must be a numeric value."}}
      between={{min: -180, max: 180, message: "Station longitude must be between -180 and 180."}}
      hint={(<React.Fragment>Please enter longitude either as degrees, minutes, seconds or as a decimal.</React.Fragment>)}/>            

  renderDepth = () => 
    <Form.Field 
      name="depth" label="Depth"
      value={this.state.data.depth}
      control={(<Input type="text" placeholder="Depth" fluid/>)}
      isFloat={{message: "Station depth must be a numeric value."}}
      hint={(<React.Fragment>Please enter a station depth in meters below tidal datum (hydrographic zero).</React.Fragment>)}/>              

  private handleCategoryClick = (cat: Stationcategory) => {
    let idx = this.state.data.stationcategories.findIndex(mycat => mycat.id == cat.id);
    if(idx == -1) {
      this.state.data.stationcategories.push(cat);
    } else {
      this.state.data.stationcategories.splice(idx, 1);
    }
    this.setState({
      data: {
        ...this.state.data,
        stationcategories: this.state.data.stationcategories
      }
    });
  }

  render() {
    let p = this.props;
    return (
      <Form
        data={this.state.data} dirty={p.dirty} onChange={p.onChange} onValidate={p.onValidate}>
        <Section padded>
          <Flex stackable>
            <Flex.Row>
              <Flex.Column width={2}>
                <Flex.Columns count={2}>
                  {this.renderName()}
                  {this.renderDescription()}
                  {this.renderLatitude()}
                  {this.renderLongitude()}
                  {this.renderDepth()}
                </Flex.Columns>
              </Flex.Column>
              <Flex.Column width={1}>
                <Form.Uncontrolled label="Categories">
                  <TreeContainer>
                    <CategoryTree 
                      items={this.state.stationcategories} 
                      active={this.state.data.stationcategories.map(sc => sc.id)}
                      onClick={this.handleCategoryClick}/>
                  </TreeContainer>
                </Form.Uncontrolled>
              </Flex.Column>
            </Flex.Row>
          </Flex>
        </Section>
        <PickLocationMap 
          zoom={11} 
          lat={this.state.data.latitude}
          lng={this.state.data.longitude}
          onSelect={(lat: number, lng: number) => {
            this.state.data.latitude = lat;
            this.state.data.longitude = lng;
            this.props.onChange(this.state.data, true);
          }}
        />
      </Form>
    )
  }
}

const TreeContainer = styled('div')`
  width: 100%;
  height: 280px;
  overflow-y: scroll;
  border: solid 1px #E0E1E2;
`

export { StationForm };
